import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Share2, Plus, Calendar, Bot, Edit, Trash2, Send, Facebook as FacebookIcon, Instagram, Twitter, Video, RefreshCw, Palette, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: FacebookIcon, color: '#1877F2' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'twitter', name: 'Twitter', icon: Twitter, color: '#1DA1F2' },
  { id: 'tiktok', name: 'TikTok', icon: Video, color: '#000000' },
  { id: 'maxpreps', name: 'MaxPreps', icon: Share2, color: '#00A86B' }
];

const POST_TYPES = [
  { value: 'pre_game', label: 'Pre-Game Hype' },
  { value: 'post_game', label: 'Post-Game Recap' },
  { value: 'tryouts', label: 'Tryouts Announcement' },
  { value: 'special_event', label: 'Special Event' },
  { value: 'practice_update', label: 'Practice Update' },
  { value: 'custom', label: 'Custom Post' }
];

export default function SocialMediaPage({ user, onLogout }) {
  const [posts, setPosts] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [canvaStatus, setCanvaStatus] = useState({ configured: false, connected: false });
  
  const [newPost, setNewPost] = useState({
    content: '',
    platforms: [],
    post_type: 'custom',
    schedule_date: '',
    event_details: ''
  });

  const [aiPrompt, setAiPrompt] = useState({
    post_type: 'pre_game',
    event_details: '',
    tone: 'energetic',
    include_hashtags: true
  });

  useEffect(() => {
    loadPosts();
    loadConnectedPlatforms();
    loadCanvaStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('canva') === 'connected') {
      toast.success('Canva connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('canva')) {
      toast.error('Canva connection failed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Handle recap-to-social draft handoff from Reports page
    const draft = params.get('draft');
    if (draft) {
      const source = params.get('source') || 'recap';
      setNewPost((prev) => ({
        ...prev,
        content: draft,
        post_type: source === 'recap' ? 'post_game' : prev.post_type,
      }));
      setShowCreateDialog(true);
      toast.info('Recap loaded as a social post draft. Pick platforms and post!');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadPosts = async () => {
    try {
      const response = await axios.get(`${API}/social-media/posts?team_id=${user.team_id}`);
      setPosts(response.data.filter(p => p.status === 'posted'));
      setScheduledPosts(response.data.filter(p => p.status === 'scheduled'));
    } catch (error) {
      console.error('Failed to load posts');
    }
  };

  const loadConnectedPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/social-media/connections?team_id=${user.team_id}`);
      setConnectedPlatforms(response.data.map(c => c.platform));
    } catch (error) {
      console.error('Failed to load connected platforms');
    }
  };

  const handleGenerateWithAI = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/social-media/generate`, {
        team_id: user.team_id,
        ...aiPrompt
      });
      
      setNewPost({
        ...newPost,
        content: response.data.content,
        post_type: aiPrompt.post_type
      });
      
      setShowAIDialog(false);
      setShowCreateDialog(true);
      toast.success('AI-generated post ready to edit!');
    } catch (error) {
      toast.error('Failed to generate post with AI');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    
    if (newPost.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    try {
      await axios.post(`${API}/social-media/posts`, {
        ...newPost,
        team_id: user.team_id,
        created_by: user.id
      });
      
      toast.success(newPost.schedule_date ? 'Post scheduled successfully!' : 'Post published successfully!');
      setShowCreateDialog(false);
      setNewPost({
        content: '',
        platforms: [],
        post_type: 'custom',
        schedule_date: '',
        event_details: ''
      });
      loadPosts();
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/social-media/posts/${postId}`);
      toast.success('Post deleted successfully!');
      loadPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const togglePlatform = (platformId) => {
    setNewPost(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const loadCanvaStatus = async () => {
    try {
      const response = await axios.get(`${API}/canva/status?user_id=${user.id}`);
      setCanvaStatus(response.data);
    } catch (error) {
      console.error('Failed to load Canva status');
    }
  };


  const createCanvaDesign = async () => {
    try {
      const response = await axios.post(`${API}/canva/design`, {
        user_id: user.id,
        title: 'Team Social Post',
        width: 1080,
        height: 1080
      });
      if (response.data.edit_url) {
        window.open(response.data.edit_url, '_blank');
        toast.success('Design created in your Canva account!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Canva design');
    }
  };

  const connectPlatform = async (platformId) => {
    toast.info(`Opening ${PLATFORMS.find(p => p.id === platformId).name} authentication...`);
    // In production, this would open OAuth flow
    setTimeout(() => {
      setConnectedPlatforms(prev => [...prev, platformId]);
      toast.success(`Connected to ${PLATFORMS.find(p => p.id === platformId).name}!`);
    }, 1500);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="social-media-page">
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Social Media Manager
            </h1>
            <p className="text-muted-foreground mt-2">AI-powered social media scheduling and posting</p>
          </div>
          <div className="flex gap-2">
            {canvaStatus.connected ? (
              <Button variant="outline" onClick={createCanvaDesign} data-testid="canva-create-design-btn" style={{ borderColor: '#8B3DFF', color: '#8B3DFF' }}>
                <Palette className="h-4 w-4 mr-2" />
                DESIGN IN CANVA
              </Button>
            ) : (
              <Button variant="outline" data-testid="canva-unavailable-btn" className="text-muted-foreground"
                onClick={() => toast.info('Canva design export is not available yet — it is on our post-launch list. Your posts and AI captions work as usual.')}>
                <Palette className="h-4 w-4 mr-2" />
                CANVA (COMING SOON)
              </Button>
            )}
            <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="ai-generate-btn">
                  <Bot className="h-4 w-4 mr-2" />
                  AI GENERATE
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                    <Bot className="h-6 w-6" />
                    AI Post Generator
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      ✨ Our AI will create engaging social media content tailored to your team and event!
                    </p>
                  </div>
                  <div>
                    <Label>Post Type</Label>
                    <Select
                      value={aiPrompt.post_type}
                      onValueChange={(value) => setAiPrompt({...aiPrompt, post_type: value})}
                    >
                      <SelectTrigger data-testid="ai-post-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {POST_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Event Details</Label>
                    <Textarea
                      data-testid="ai-event-details"
                      value={aiPrompt.event_details}
                      onChange={(e) => setAiPrompt({...aiPrompt, event_details: e.target.value})}
                      placeholder="e.g., vs. Central High, Championship Game, Friday 7PM..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Tone</Label>
                    <Select
                      value={aiPrompt.tone}
                      onValueChange={(value) => setAiPrompt({...aiPrompt, tone: value})}
                    >
                      <SelectTrigger data-testid="ai-tone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="energetic">Energetic & Hype</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="inspirational">Inspirational</SelectItem>
                        <SelectItem value="casual">Casual & Fun</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="hashtags"
                      checked={aiPrompt.include_hashtags}
                      onChange={(e) => setAiPrompt({...aiPrompt, include_hashtags: e.target.checked})}
                      data-testid="ai-hashtags"
                    />
                    <Label htmlFor="hashtags">Include hashtags</Label>
                  </div>
                  <Button 
                    onClick={handleGenerateWithAI} 
                    className="w-full" 
                    disabled={isGenerating}
                    data-testid="generate-ai-post-btn"
                  >
                    {isGenerating ? (
                      <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />GENERATING...</>
                    ) : (
                      <><Bot className="h-4 w-4 mr-2" />GENERATE POST</>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-post-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  CREATE POST
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase">Create Social Media Post</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreatePost} className="space-y-4">
                  <div>
                    <Label>Post Type</Label>
                    <Select
                      value={newPost.post_type}
                      onValueChange={(value) => setNewPost({...newPost, post_type: value})}
                    >
                      <SelectTrigger data-testid="post-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {POST_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Content</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-orange-500 hover:text-orange-600"
                        onClick={async () => {
                          if (!newPost.content) {
                            toast.error('Nothing to copy');
                            return;
                          }
                          try {
                            await navigator.clipboard.writeText(newPost.content);
                            toast.success('Post content copied to clipboard');
                          } catch {
                            toast.error('Could not copy — your browser blocked clipboard access');
                          }
                        }}
                        data-testid="copy-post-content-btn"
                        disabled={!newPost.content}
                      >
                        <Copy className="h-3.5 w-3.5 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <Textarea
                      data-testid="post-content"
                      value={newPost.content}
                      onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                      placeholder="Write your post content..."
                      rows={6}
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {newPost.content.length} characters
                    </p>
                  </div>
                  <div>
                    <Label>Select Platforms</Label>
                    <div className="grid grid-cols-5 gap-2 mt-2">
                      {PLATFORMS.map(platform => {
                        const Icon = platform.icon;
                        const isSelected = newPost.platforms.includes(platform.id);
                        const isConnected = connectedPlatforms.includes(platform.id);
                        
                        return (
                          <button
                            key={platform.id}
                            type="button"
                            onClick={() => isConnected ? togglePlatform(platform.id) : connectPlatform(platform.id)}
                            className={`p-4 border-2 rounded-lg transition-all ${
                              isSelected 
                                ? 'border-primary bg-primary/10' 
                                : isConnected
                                ? 'border-muted-foreground/20 hover:border-primary/50'
                                : 'border-dashed border-muted-foreground/20 opacity-60'
                            }`}
                            data-testid={`platform-${platform.id}`}
                          >
                            <Icon className="h-6 w-6 mx-auto mb-2" style={{ color: platform.color }} />
                            <p className="text-xs font-semibold">{platform.name}</p>
                            {!isConnected && (
                              <p className="text-xs text-muted-foreground mt-1">Connect</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label>Schedule (Optional)</Label>
                    <Input
                      type="datetime-local"
                      data-testid="schedule-date"
                      value={newPost.schedule_date}
                      onChange={(e) => setNewPost({...newPost, schedule_date: e.target.value})}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Leave empty to post immediately
                    </p>
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-post-btn">
                    <Send className="h-4 w-4 mr-2" />
                    {newPost.schedule_date ? 'SCHEDULE POST' : 'POST NOW'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Platform Connections */}
        <Card className="border-2">
          <CardHeader className="border-b bg-muted/30">
            <CardTitle className="font-heading uppercase text-2xl">Connected Platforms</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-5 gap-4">
              {PLATFORMS.map(platform => {
                const Icon = platform.icon;
                const isConnected = connectedPlatforms.includes(platform.id);
                
                return (
                  <div
                    key={platform.id}
                    className={`p-4 border-2 rounded-lg text-center ${
                      isConnected ? 'border-green-500 bg-green-50' : 'border-muted-foreground/20'
                    }`}
                    data-testid={`connection-${platform.id}`}
                  >
                    <Icon className="h-8 w-8 mx-auto mb-2" style={{ color: platform.color }} />
                    <p className="text-sm font-semibold mb-2">{platform.name}</p>
                    {isConnected ? (
                      <Badge variant="default" className="bg-green-600">Connected</Badge>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => connectPlatform(platform.id)}
                      >
                        Connect
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Posts Tabs */}
        <Tabs defaultValue="scheduled" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="scheduled">Scheduled Posts ({scheduledPosts.length})</TabsTrigger>
            <TabsTrigger value="posted">Posted ({posts.length})</TabsTrigger>
          </TabsList>
          
          <TabsContent value="scheduled" className="space-y-4 mt-4">
            {scheduledPosts.length === 0 ? (
              <Card className="border-2">
                <CardContent className="p-12 text-center">
                  <Calendar className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                  <h3 className="font-heading text-2xl mt-4">No Scheduled Posts</h3>
                  <p className="text-muted-foreground mt-2">Schedule posts to automatically publish later</p>
                </CardContent>
              </Card>
            ) : (
              scheduledPosts.map(post => (
                <Card key={post.id} className="border-2" data-testid={`scheduled-post-${post.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge>{POST_TYPES.find(t => t.value === post.post_type)?.label}</Badge>
                          <span className="text-sm text-muted-foreground">
                            Scheduled for {format(new Date(post.schedule_date), 'PPp')}
                          </span>
                        </div>
                        <p className="text-sm mb-3 whitespace-pre-wrap">{post.content}</p>
                        <div className="flex gap-2">
                          {post.platforms.map(platformId => {
                            const platform = PLATFORMS.find(p => p.id === platformId);
                            const Icon = platform.icon;
                            return (
                              <Icon key={platformId} className="h-4 w-4" style={{ color: platform.color }} />
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="icon">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="icon"
                          onClick={() => handleDeletePost(post.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
          
          <TabsContent value="posted" className="space-y-4 mt-4">
            {posts.length === 0 ? (
              <Card className="border-2">
                <CardContent className="p-12 text-center">
                  <Share2 className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                  <h3 className="font-heading text-2xl mt-4">No Posts Yet</h3>
                  <p className="text-muted-foreground mt-2">Create your first social media post</p>
                </CardContent>
              </Card>
            ) : (
              posts.map(post => (
                <Card key={post.id} className="border-2" data-testid={`posted-post-${post.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">{POST_TYPES.find(t => t.value === post.post_type)?.label}</Badge>
                          <span className="text-sm text-muted-foreground">
                            Posted {format(new Date(post.created_at), 'PPp')}
                          </span>
                        </div>
                        <p className="text-sm mb-3 whitespace-pre-wrap">{post.content}</p>
                        <div className="flex gap-2">
                          {post.platforms.map(platformId => {
                            const platform = PLATFORMS.find(p => p.id === platformId);
                            const Icon = platform.icon;
                            return (
                              <Icon key={platformId} className="h-4 w-4" style={{ color: platform.color }} />
                            );
                          })}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDeletePost(post.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}